import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Swords, User, Trophy, ArrowLeftRight } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

export default function Compare() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [myToken, setMyToken] = useState(user?.share_token || "");
  const [tokenA, setTokenA] = useState(searchParams.get("a") || "");
  const [tokenB, setTokenB] = useState(searchParams.get("b") || "");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Load my own share token so we can pre-fill it
  useEffect(() => {
    async function loadMine() {
      try {
        const { data: me } = await api.get("/auth/me");
        if (me?.share_token) setMyToken(me.share_token);
      } catch (_) { /* ignore */ }
    }
    loadMine();
  }, []);

  const runCompare = async (e) => {
    e?.preventDefault?.();
    if (!tokenA || !tokenB) {
      setError("Both share codes are required.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const { data: res } = await api.get(`/compare?a=${encodeURIComponent(tokenA)}&b=${encodeURIComponent(tokenB)}`);
      setData(res);
      setSearchParams({ a: tokenA, b: tokenB });
    } catch (err) {
      setError(formatApiError(err));
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const useMyToken = () => {
    if (!myToken) {
      toast.error("Enable your public share link in Profile first.");
      return;
    }
    setTokenA(myToken);
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="bg-[#141414] border border-[#262626] p-6 rounded-xl flex items-center gap-3">
        <div className="w-12 h-12 rounded bg-[#FF3B30]/20 border border-[#FF3B30]/40 flex items-center justify-center text-[#FF3B30]">
          <Swords className="w-6 h-6" />
        </div>
        <div>
          <h3 className="text-2xl font-black uppercase text-white">Head-to-Head Compare</h3>
          <p className="text-xs text-[#A3A3A3]">Pit two players side-by-side using their public share codes.</p>
        </div>
      </div>

      <form onSubmit={runCompare} className="bg-[#141414] border border-[#262626] rounded-xl p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="flex items-center justify-between text-xs font-mono text-[#A3A3A3] uppercase mb-1">
              Player A Share Code
              <button type="button" onClick={useMyToken} data-testid="use-my-token-btn" className="text-[10px] text-[#FF3B30] font-bold hover:underline">
                Use my code
              </button>
            </label>
            <input
              data-testid="compare-token-a"
              type="text"
              value={tokenA}
              onChange={(e) => setTokenA(e.target.value.trim())}
              placeholder="paste share code..."
              className="w-full bg-[#1A1A1A] border border-[#262626] rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-[#FF3B30] font-mono"
            />
          </div>
          <div>
            <label className="block text-xs font-mono text-[#A3A3A3] uppercase mb-1">Player B Share Code</label>
            <input
              data-testid="compare-token-b"
              type="text"
              value={tokenB}
              onChange={(e) => setTokenB(e.target.value.trim())}
              placeholder="paste rival share code..."
              className="w-full bg-[#1A1A1A] border border-[#262626] rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-[#FF3B30] font-mono"
            />
          </div>
        </div>
        {error && (
          <div data-testid="compare-error" className="bg-[#FF3B30]/10 border border-[#FF3B30]/40 text-[#FF3B30] text-xs rounded px-3 py-2">
            {error}
          </div>
        )}
        <div className="flex justify-end">
          <button
            data-testid="compare-run-btn"
            type="submit"
            disabled={loading}
            className="bg-[#FF3B30] hover:bg-[#E03229] disabled:opacity-50 text-white font-bold px-6 py-2.5 rounded text-sm flex items-center gap-2 shadow-lg"
          >
            <ArrowLeftRight className="w-4 h-4" /> {loading ? "Comparing..." : "Run Compare"}
          </button>
        </div>
      </form>

      {data && <CompareResults data={data} />}
    </div>
  );
}

function CompareResults({ data }) {
  const A = data.a;
  const B = data.b;
  const sA = A.analytics.summary;
  const sB = B.analytics.summary;

  const rows = [
    { key: "total_matches", label: "Matches", better: "higher" },
    { key: "total_runs", label: "Total Runs", better: "higher" },
    { key: "batting_average", label: "Batting Average", better: "higher" },
    { key: "strike_rate", label: "Strike Rate", better: "higher" },
    { key: "boundary_percentage", label: "Boundary %", better: "higher" },
    { key: "best_score", label: "Best Score", better: "higher" },
    { key: "total_wickets", label: "Wickets", better: "higher" },
    { key: "bowling_economy", label: "Bowling Economy", better: "lower" },
    { key: "consistency_score", label: "Consistency", better: "higher" },
  ];

  const winner = (row) => {
    const va = sA[row.key] ?? 0;
    const vb = sB[row.key] ?? 0;
    if (va === vb) return null;
    if (row.better === "higher") return va > vb ? "a" : "b";
    return va < vb ? "a" : "b";
  };

  const wins = { a: 0, b: 0 };
  rows.forEach((r) => {
    const w = winner(r);
    if (w) wins[w] += 1;
  });

  const chartData = [
    { metric: "Runs", A: sA.total_runs, B: sB.total_runs },
    { metric: "Avg", A: sA.batting_average, B: sB.batting_average },
    { metric: "SR", A: sA.strike_rate, B: sB.strike_rate },
    { metric: "Best", A: sA.best_score, B: sB.best_score },
    { metric: "Wkts", A: sA.total_wickets, B: sB.total_wickets },
  ];

  return (
    <div className="space-y-6" data-testid="compare-results">
      {/* Headers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PlayerHeader p={A.player} isWinner={wins.a > wins.b} wins={wins.a} testid="compare-player-a" />
        <PlayerHeader p={B.player} isWinner={wins.b > wins.a} wins={wins.b} testid="compare-player-b" />
      </div>

      {/* Metric-by-metric table */}
      <div className="bg-[#141414] border border-[#262626] rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-[#262626] flex items-center justify-between">
          <h4 className="text-lg font-bold uppercase text-white">Metric Breakdown</h4>
          <span className="text-xs font-mono text-[#A3A3A3]">
            Winner highlighted in <span className="text-emerald-400 font-bold">green</span>
          </span>
        </div>
        <div className="divide-y divide-[#262626]">
          {rows.map((r) => {
            const va = sA[r.key] ?? 0;
            const vb = sB[r.key] ?? 0;
            const w = winner(r);
            return (
              <div key={r.key} className="grid grid-cols-3 items-center px-6 py-3 text-sm">
                <div className={`text-right pr-4 font-bold ${w === "a" ? "text-emerald-400" : "text-white"}`}>{va}</div>
                <div className="text-center text-xs font-mono text-[#A3A3A3] uppercase">{r.label}</div>
                <div className={`text-left pl-4 font-bold ${w === "b" ? "text-emerald-400" : "text-white"}`}>{vb}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Bar chart */}
      <div className="bg-[#141414] border border-[#262626] rounded-xl p-6">
        <h4 className="text-lg font-bold uppercase text-white mb-4">Side-by-Side Chart</h4>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <XAxis dataKey="metric" stroke="#737373" fontSize={12} />
              <YAxis stroke="#737373" fontSize={12} />
              <Tooltip contentStyle={{ backgroundColor: "#141414", borderColor: "#262626", borderRadius: 8 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="A" fill="#FF3B30" name={A.player.name} radius={[4, 4, 0, 0]} />
              <Bar dataKey="B" fill="#007AFF" name={B.player.name} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function PlayerHeader({ p, isWinner, wins, testid }) {
  return (
    <div data-testid={testid} className={`bg-[#141414] border rounded-xl p-6 flex items-center gap-4 ${isWinner ? "border-emerald-500/50 shadow-[0_0_25px_rgba(52,199,89,0.15)]" : "border-[#262626]"}`}>
      <div className="w-14 h-14 rounded-full bg-[#FF3B30] flex items-center justify-center text-white font-black text-lg uppercase">
        {(p.name || "P").slice(0, 2)}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <h3 className="text-xl font-black uppercase text-white">{p.name}</h3>
          {isWinner && (
            <span className="text-emerald-400 text-xs font-bold flex items-center gap-1 bg-emerald-500/20 px-2 py-0.5 rounded border border-emerald-500/40">
              <Trophy className="w-3 h-3" /> Leading
            </span>
          )}
        </div>
        <p className="text-xs text-[#A3A3A3] mt-0.5">
          {p.profile?.playing_role} • {p.profile?.batting_style}
          {p.profile?.team ? ` • ${p.profile.team}` : ""}
        </p>
        <p className="text-xs text-[#A3A3A3] mt-2 font-mono">
          Wins in {wins} metric{wins !== 1 ? "s" : ""}
        </p>
      </div>
    </div>
  );
}
